import type { FilterGroup } from "@estelle/domain";

import { cn } from "@/lib/utils";

/**
 * The word between two conditions — AND or OR — as a button that flips it.
 *
 * "Status is any of … AND Shipped is between …" has to be readable off the
 * screen and changeable in one click, in the filter bar and in the tree
 * editor alike (#492). It was neither: the bar showed an opaque "N conditions
 * (advanced)" for anything with an OR in it, and the editor's one AND/OR
 * switch sat in a header, away from the rows it governed.
 *
 * It flips the whole group's combinator, not just the one connector, which is
 * why every connector in a group reads the same and changes together. A group
 * has one combinator; letting each gap differ is the "a and b or c" ambiguity
 * that grouping exists to remove. OR is the one drawn in colour, since it is
 * the one that widens the list.
 *
 * At least 24px tall, which is what a pointer needs to hit it without trying;
 * a caller on a touch layout asks for more through `className`.
 */
export function CombinatorToggle({
  combinator,
  onToggle,
  className,
}: {
  combinator: FilterGroup["combinator"];
  onToggle: () => void;
  className?: string;
}) {
  const other = combinator === "and" ? "OR" : "AND";

  return (
    <button
      type="button"
      onClick={onToggle}
      aria-label={`Conditions are joined with ${combinator.toUpperCase()}. Switch to ${other}`}
      title={
        combinator === "and"
          ? "Every condition must match. Click to match any instead."
          : "Any condition may match. Click to require every one instead."
      }
      className={cn(
        "focus-visible:ring-ring inline-flex min-h-6 min-w-8 shrink-0 items-center justify-center rounded px-1.5 text-[10px] font-semibold uppercase transition-colors focus-visible:ring-2 focus-visible:outline-none",
        combinator === "or"
          ? "bg-primary/15 text-primary hover:bg-primary/25"
          : "text-muted-foreground hover:bg-accent hover:text-foreground",
        className,
      )}
    >
      {combinator}
    </button>
  );
}
